/**
 * Export helpers — Excel (xlsx) y PDF.
 * Solo funciona en web (la API de descarga usa Blob/saveAs del navegador).
 */
import { Platform } from 'react-native';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import type { MovimientoContable, Bloque, PetshopVenta, PetshopRendicion, PetshopId, Solicitud } from '@/types/domain';
import { PETSHOPS } from '@/types/domain';
import { formatBs, formatDate, formatDateTime } from '@/utils/format';
import { areaNombre } from '@/utils/areas';

const BLOQUE_LABEL: Record<Bloque, string> = {
  clinica: 'Clínica',
};

const METODO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo',
  qr: 'QR',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  otro: 'Otro',
};

interface ExportContext {
  bloque: Bloque;
  movimientos: MovimientoContable[];
  desde?: Date;
  hasta?: Date;
}

function rangoTexto({ desde, hasta }: { desde?: Date; hasta?: Date }) {
  if (!desde && !hasta) return 'Histórico completo';
  return `${desde ? formatDate(desde) : '—'}  →  ${hasta ? formatDate(hasta) : 'hoy'}`;
}

// =============================================================================
// EXCEL
// =============================================================================

export async function exportContabilidadExcel(ctx: ExportContext) {
  if (Platform.OS !== 'web') {
    throw new Error('La exportación a Excel solo está disponible en la versión web por ahora.');
  }

  // Calcular totales primero (van arriba).
  let totalIngresos = 0;
  let totalEgresos = 0;
  ctx.movimientos.forEach((m) => {
    if (m.tipo === 'ingreso') totalIngresos += m.monto;
    else totalEgresos += m.monto;
  });
  const balance = totalIngresos - totalEgresos;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'CliniCAT';
  wb.created = new Date();
  wb.company = 'CliniCAT — Medicina Felina';

  const sheet = wb.addWorksheet(`Contabilidad ${BLOQUE_LABEL[ctx.bloque]}`, {
    views: [{ state: 'frozen', ySplit: 9 }],
  });

  // ───────────────────────────── HEADER ──────────────────────────────
  // Fila 1 — título
  sheet.mergeCells('A1:F1');
  const titulo = sheet.getCell('A1');
  titulo.value = 'CliniCAT — Reporte de contabilidad';
  titulo.font = { bold: true, size: 18, color: { argb: 'FFFFFFFF' } };
  titulo.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B1F47' } };
  titulo.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheet.getRow(1).height = 32;

  // Fila 2 — subtítulo (bloque + período)
  sheet.mergeCells('A2:F2');
  const sub = sheet.getCell('A2');
  sub.value = `Bloque ${BLOQUE_LABEL[ctx.bloque]}  ·  ${rangoTexto(ctx)}`;
  sub.font = { size: 11, color: { argb: 'FFFFFFFF' }, italic: true };
  sub.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF8B1B5E' } };
  sub.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheet.getRow(2).height = 22;

  // Fila 3 — generado
  sheet.mergeCells('A3:F3');
  const gen = sheet.getCell('A3');
  gen.value = `Generado el ${formatDateTime(new Date())}  ·  ${ctx.movimientos.length} movimiento${ctx.movimientos.length === 1 ? '' : 's'}`;
  gen.font = { size: 9, color: { argb: 'FF94A3B8' } };
  gen.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheet.getRow(3).height = 18;

  // Fila 4 — espacio
  sheet.getRow(4).height = 8;

  // ───────────────────────────── TOTALES ARRIBA ──────────────────────
  // Fila 5 — etiquetas
  const labelsRow = sheet.getRow(5);
  labelsRow.values = ['', 'INGRESOS', '', 'EGRESOS', '', 'BALANCE'];
  ['B5', 'D5', 'F5'].forEach((addr) => {
    const c = sheet.getCell(addr);
    c.font = { bold: true, size: 10, color: { argb: 'FF64748B' } };
    c.alignment = { horizontal: 'center', vertical: 'middle' };
  });
  labelsRow.height = 18;

  // Fila 6 — valores grandes
  const valuesRow = sheet.getRow(6);
  valuesRow.values = ['', totalIngresos, '', totalEgresos, '', balance];
  const ing = sheet.getCell('B6');
  ing.font = { bold: true, size: 18, color: { argb: 'FF0F9D58' } };
  ing.alignment = { horizontal: 'center', vertical: 'middle' };
  ing.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F8EE' } };
  ing.numFmt = '"Bs "#,##0.00';
  ing.border = { top: { style: 'medium', color: { argb: 'FF0F9D58' } } };

  const eg = sheet.getCell('D6');
  eg.font = { bold: true, size: 18, color: { argb: 'FFDC2626' } };
  eg.alignment = { horizontal: 'center', vertical: 'middle' };
  eg.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFDECEC' } };
  eg.numFmt = '"Bs "#,##0.00';
  eg.border = { top: { style: 'medium', color: { argb: 'FFDC2626' } } };

  const bal = sheet.getCell('F6');
  bal.font = { bold: true, size: 18, color: { argb: balance >= 0 ? 'FF7B1F47' : 'FFDC2626' } };
  bal.alignment = { horizontal: 'center', vertical: 'middle' };
  bal.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCE7F1' } };
  bal.numFmt = '"Bs "#,##0.00;[Red]-"Bs "#,##0.00';
  bal.border = { top: { style: 'medium', color: { argb: 'FF7B1F47' } } };
  valuesRow.height = 32;

  // Fila 7 — espacio
  sheet.getRow(7).height = 12;

  // Fila 8 — etiqueta de la tabla
  sheet.mergeCells('A8:F8');
  const tablaLbl = sheet.getCell('A8');
  tablaLbl.value = 'DETALLE DE MOVIMIENTOS';
  tablaLbl.font = { bold: true, size: 11, color: { argb: 'FF7B1F47' } };
  tablaLbl.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheet.getRow(8).height = 22;

  // Fila 9 — headers de la tabla
  const headerRow = sheet.getRow(9);
  headerRow.values = ['Fecha', 'Tipo', 'Categoría', 'Descripción', 'Método pago', 'Monto (Bs)'];
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5C0F35' } };
    cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
    };
  });
  headerRow.getCell(6).alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
  headerRow.height = 22;

  // ───────────────────────────── FILAS ───────────────────────────────
  // Ordenar por fecha desc
  const ordenadas = [...ctx.movimientos].sort((a, b) => b.fecha.toMillis() - a.fecha.toMillis());

  ordenadas.forEach((m, idx) => {
    const fecha = m.fecha.toDate();
    const monto = m.tipo === 'ingreso' ? m.monto : -m.monto;
    const row = sheet.addRow([
      fecha,
      m.tipo === 'ingreso' ? 'Ingreso' : 'Egreso',
      m.categoria,
      m.descripcion,
      METODO_LABEL[m.metodoPago] ?? m.metodoPago,
      monto,
    ]);

    row.eachCell((cell, col) => {
      cell.alignment = { horizontal: col === 6 ? 'right' : 'left', vertical: 'middle', indent: 1 };
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } } };
      if (idx % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF7F4' } };
    });

    row.getCell(1).numFmt = 'dd/mm/yyyy';
    row.getCell(2).font = {
      color: { argb: m.tipo === 'ingreso' ? 'FF0F9D58' : 'FFDC2626' },
      bold: true,
      size: 10,
    };
    row.getCell(6).numFmt = '"Bs "#,##0.00;[Red]-"Bs "#,##0.00';
    row.getCell(6).font = {
      bold: true,
      color: { argb: m.tipo === 'ingreso' ? 'FF0F9D58' : 'FFDC2626' },
    };
    row.height = 18;
  });

  // ───────────────────────── ANCHOS COLUMNAS ─────────────────────────
  [13, 11, 22, 42, 16, 18].forEach((w, i) => {
    sheet.getColumn(i + 1).width = w;
  });

  // ─────────────────────────── DESCARGAR ─────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `CliniCAT_Contabilidad_${BLOQUE_LABEL[ctx.bloque]}_${ymd(new Date())}.xlsx`);
}

// =============================================================================
// PDF
// =============================================================================

export async function exportContabilidadPDF(ctx: ExportContext) {
  if (Platform.OS !== 'web') {
    throw new Error('La exportación a PDF solo está disponible en la versión web por ahora.');
  }

  // Totales primero
  let totalIngresos = 0;
  let totalEgresos = 0;
  ctx.movimientos.forEach((m) => {
    if (m.tipo === 'ingreso') totalIngresos += m.monto;
    else totalEgresos += m.monto;
  });
  const balance = totalIngresos - totalEgresos;

  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 24;
  const contentWidth = pageWidth - margin * 2;

  // ───────────────────── HEADER MAGENTA ─────────────────────
  doc.setFillColor(91, 15, 53); // magenta-800
  doc.rect(0, 0, pageWidth, 88, 'F');

  doc.setFillColor(123, 31, 71); // magenta-700
  doc.rect(0, 0, pageWidth * 0.55, 88, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('CliniCAT', margin, 36);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 241, 248);
  doc.text('Medicina Felina', margin, 52);
  doc.setFontSize(8);
  doc.text('Calle Los Motojobobos #2015 · +591 60008504', margin, 66);

  // Lado derecho del header — info del reporte
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text('REPORTE DE CONTABILIDAD', pageWidth - margin, 36, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Bloque ${BLOQUE_LABEL[ctx.bloque]}`, pageWidth - margin, 52, { align: 'right' });
  doc.setFontSize(8);
  doc.setTextColor(232, 182, 207);
  doc.text(rangoTexto(ctx), pageWidth - margin, 66, { align: 'right' });
  doc.text(`Generado: ${formatDate(new Date())}`, pageWidth - margin, 78, { align: 'right' });

  // ───────────────────── TOTALES ARRIBA (cards) ─────────────────────
  const cardsY = 110;
  const cardH = 60;
  const gap = 10;
  const cardW = (contentWidth - gap * 2) / 3;

  // Helper para card
  const drawTotalCard = (
    x: number,
    label: string,
    valor: number,
    accentRGB: [number, number, number],
    bgRGB: [number, number, number],
  ) => {
    // Fondo
    doc.setFillColor(...bgRGB);
    doc.roundedRect(x, cardsY, cardW, cardH, 8, 8, 'F');
    // Borde superior accent
    doc.setFillColor(...accentRGB);
    doc.roundedRect(x, cardsY, cardW, 4, 2, 2, 'F');
    // Etiqueta
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(label.toUpperCase(), x + 14, cardsY + 22);
    // Valor
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...accentRGB);
    doc.text(formatBs(valor), x + 14, cardsY + 46);
  };

  drawTotalCard(margin, 'Ingresos', totalIngresos, [15, 157, 88], [232, 248, 238]);
  drawTotalCard(margin + cardW + gap, 'Egresos', totalEgresos, [220, 38, 38], [253, 236, 236]);
  drawTotalCard(
    margin + (cardW + gap) * 2,
    'Balance',
    balance,
    balance >= 0 ? [123, 31, 71] : [220, 38, 38],
    [252, 231, 241],
  );

  // ───────────────────── TÍTULO TABLA ─────────────────────
  const tableHeader = cardsY + cardH + 26;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(123, 31, 71);
  doc.text(`DETALLE DE MOVIMIENTOS`, margin, tableHeader);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `${ctx.movimientos.length} ${ctx.movimientos.length === 1 ? 'registro' : 'registros'}`,
    pageWidth - margin,
    tableHeader,
    { align: 'right' },
  );

  // Línea separadora
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.line(margin, tableHeader + 6, pageWidth - margin, tableHeader + 6);

  // ───────────────────── TABLA ─────────────────────
  const ordenadas = [...ctx.movimientos].sort((a, b) => b.fecha.toMillis() - a.fecha.toMillis());

  const filas = ordenadas.map((m) => [
    formatDate(m.fecha.toDate()),
    m.tipo === 'ingreso' ? 'Ingreso' : 'Egreso',
    m.categoria,
    m.descripcion,
    METODO_LABEL[m.metodoPago] ?? m.metodoPago,
    `${m.tipo === 'ingreso' ? '+' : '−'} ${formatBs(m.monto)}`,
  ]);

  autoTable(doc, {
    startY: tableHeader + 14,
    head: [['Fecha', 'Tipo', 'Categoría', 'Descripción', 'Método', 'Monto']],
    body: filas,
    theme: 'plain',
    headStyles: {
      fillColor: [91, 15, 53],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: { top: 7, bottom: 7, left: 6, right: 6 },
      lineWidth: 0,
    },
    bodyStyles: {
      fontSize: 8.5,
      cellPadding: { top: 6, bottom: 6, left: 6, right: 6 },
      lineWidth: 0.3,
      lineColor: [240, 240, 240],
      textColor: [30, 41, 59],
      overflow: 'linebreak',
    },
    alternateRowStyles: { fillColor: [250, 247, 244] },
    columnStyles: {
      0: { cellWidth: 56 },
      1: { cellWidth: 46, fontStyle: 'bold' },
      2: { cellWidth: 72 },
      3: { cellWidth: 'auto' },
      4: { cellWidth: 70 },
      5: { cellWidth: 92, halign: 'right', fontStyle: 'bold' },
    },
    margin: { left: margin, right: margin, top: 92 },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      const tipo = data.row.raw[1];
      if (data.column.index === 1 || data.column.index === 5) {
        if (tipo === 'Ingreso') data.cell.styles.textColor = [15, 157, 88];
        else if (tipo === 'Egreso') data.cell.styles.textColor = [220, 38, 38];
      }
    },
    didDrawPage: () => {
      // Footer en cada página
      const pageNum = (doc.internal as any).getCurrentPageInfo().pageNumber;
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('CliniCAT — Medicina Felina', margin, pageHeight - 18);
      doc.text(`Página ${pageNum}`, pageWidth - margin, pageHeight - 18, { align: 'right' });
    },
  });

  doc.save(`CliniCAT_Contabilidad_${BLOQUE_LABEL[ctx.bloque]}_${ymd(new Date())}.pdf`);
}

function ymd(d: Date) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// =============================================================================
// ENTREGAS DE SOLICITUDES — historial (qué se entregó, a quién, cuánto, cuándo)
// =============================================================================
interface EntregasExportContext {
  solicitudes: Solicitud[];
  desde?: Date;
  hasta?: Date;
}

function filasEntregas(solicitudes: Solicitud[]) {
  const filas: { fecha: Date | null; area: string; pidio: string; insumo: string; entregado: number }[] = [];
  solicitudes.forEach((s) => {
    const any = s as any;
    const fecha = any.entregadaEn?.toDate?.() ?? any.actualizadaEn?.toDate?.() ?? any.creadaEn?.toDate?.() ?? null;
    (s.items ?? []).forEach((it) => {
      const ent = it.cantidadEntregada ?? 0;
      if (ent > 0) {
        filas.push({
          fecha,
          area: any.areaNombre ?? areaNombre(any.area ?? ''),
          pidio: s.doctorNombre ?? any.ultimoEditorNombre ?? '—',
          insumo: it.insumoNombre,
          entregado: ent,
        });
      }
    });
  });
  return filas.sort((a, b) => (b.fecha?.getTime() ?? 0) - (a.fecha?.getTime() ?? 0));
}

export async function exportEntregasExcel(ctx: EntregasExportContext) {
  if (Platform.OS !== 'web') throw new Error('Solo disponible en web por ahora.');
  const filas = filasEntregas(ctx.solicitudes);
  const totalUnidades = filas.reduce((s, f) => s + f.entregado, 0);

  const wb = new ExcelJS.Workbook();
  wb.creator = 'CliniCAT';
  wb.created = new Date();
  const sheet = wb.addWorksheet('Entregas', { views: [{ state: 'frozen', ySplit: 5 }] });

  sheet.mergeCells('A1:E1');
  const t = sheet.getCell('A1');
  t.value = 'CliniCAT — Historial de entregas';
  t.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
  t.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B1F47' } };
  t.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheet.getRow(1).height = 30;

  sheet.mergeCells('A2:E2');
  const sub = sheet.getCell('A2');
  sub.value = `${rangoTexto(ctx)}  ·  Generado el ${formatDateTime(new Date())}`;
  sub.font = { size: 10, italic: true, color: { argb: 'FFFFFFFF' } };
  sub.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF8B1B5E' } };
  sub.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheet.getRow(2).height = 20;

  sheet.mergeCells('A3:E3');
  sheet.getCell('A3').value = `${filas.length} ${filas.length === 1 ? 'entrega' : 'entregas'}  ·  ${totalUnidades} unidades en total`;
  sheet.getCell('A3').font = { size: 10, color: { argb: 'FF64748B' } };
  sheet.getCell('A3').alignment = { horizontal: 'left', indent: 1 };
  sheet.getRow(4).height = 8;

  const header = sheet.getRow(5);
  header.values = ['Fecha de entrega', 'Área', 'Solicitado por', 'Insumo', 'Entregado'];
  header.eachCell((cell, col) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5C0F35' } };
    cell.alignment = { horizontal: col === 5 ? 'right' : 'left', vertical: 'middle', indent: 1 };
  });
  header.height = 22;

  filas.forEach((f, idx) => {
    const row = sheet.addRow([f.fecha ?? '', f.area, f.pidio, f.insumo, f.entregado]);
    row.eachCell((cell, col) => {
      cell.alignment = { horizontal: col === 5 ? 'right' : 'left', vertical: 'middle', indent: 1 };
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } } };
      if (idx % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF7F4' } };
    });
    if (f.fecha) row.getCell(1).numFmt = 'dd/mm/yyyy hh:mm';
    row.getCell(5).font = { bold: true, color: { argb: 'FF0F9D58' } };
    row.height = 18;
  });

  [22, 18, 22, 40, 14].forEach((w, i) => (sheet.getColumn(i + 1).width = w));

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `CliniCAT_Entregas_${ymd(new Date())}.xlsx`);
}

export async function exportEntregasPDF(ctx: EntregasExportContext) {
  if (Platform.OS !== 'web') throw new Error('Solo disponible en web por ahora.');
  const filas = filasEntregas(ctx.solicitudes);
  const totalUnidades = filas.reduce((s, f) => s + f.entregado, 0);

  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 24;

  doc.setFillColor(91, 15, 53);
  doc.rect(0, 0, pageWidth, 80, 'F');
  doc.setFillColor(123, 31, 71);
  doc.rect(0, 0, pageWidth * 0.55, 80, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('CliniCAT', margin, 34);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(255, 241, 248);
  doc.text('Medicina Felina', margin, 50);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text('HISTORIAL DE ENTREGAS', pageWidth - margin, 34, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(232, 182, 207);
  doc.text(rangoTexto(ctx), pageWidth - margin, 50, { align: 'right' });
  doc.text(`${filas.length} entregas · ${totalUnidades} unidades`, pageWidth - margin, 62, { align: 'right' });

  const bodyFilas = filas.map((f) => [
    f.fecha ? formatDateTime(f.fecha) : '—',
    f.area,
    f.pidio,
    f.insumo,
    String(f.entregado),
  ]);

  autoTable(doc, {
    startY: 100,
    head: [['Fecha de entrega', 'Área', 'Solicitado por', 'Insumo', 'Entregado']],
    body: bodyFilas,
    theme: 'plain',
    headStyles: { fillColor: [91, 15, 53], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9, cellPadding: { top: 7, bottom: 7, left: 6, right: 6 } },
    bodyStyles: { fontSize: 8.5, cellPadding: { top: 6, bottom: 6, left: 6, right: 6 }, lineWidth: 0.3, lineColor: [240, 240, 240], textColor: [30, 41, 59], overflow: 'linebreak' },
    alternateRowStyles: { fillColor: [250, 247, 244] },
    columnStyles: { 0: { cellWidth: 96 }, 1: { cellWidth: 70 }, 2: { cellWidth: 90 }, 3: { cellWidth: 'auto' }, 4: { cellWidth: 60, halign: 'right', fontStyle: 'bold', textColor: [15, 157, 88] } },
    margin: { left: margin, right: margin, top: 92 },
    didDrawPage: () => {
      const pageNum = (doc.internal as any).getCurrentPageInfo().pageNumber;
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('CliniCAT — Medicina Felina', margin, pageHeight - 18);
      doc.text(`Página ${pageNum}`, pageWidth - margin, pageHeight - 18, { align: 'right' });
    },
  });

  doc.save(`CliniCAT_Entregas_${ymd(new Date())}.pdf`);
}

// =============================================================================
// HISTORIAL DE USO — salidas de insumos internos (qué, cuánto, detalle, área, quién, cuándo)
// =============================================================================
export interface FilaHistorialUso {
  fecha: Date | null;
  producto: string;
  cantidad: number;
  detalle: string;
  area: string;
  quien: string;
}

interface HistorialUsoExportContext {
  filas: FilaHistorialUso[];
  desde?: Date;
  hasta?: Date;
}

export async function exportHistorialUsoExcel(ctx: HistorialUsoExportContext) {
  if (Platform.OS !== 'web') throw new Error('Solo disponible en web por ahora.');
  const filas = ctx.filas;
  const totalUnidades = filas.reduce((s, f) => s + f.cantidad, 0);

  const wb = new ExcelJS.Workbook();
  wb.creator = 'CliniCAT';
  wb.created = new Date();
  const sheet = wb.addWorksheet('Uso de insumos', { views: [{ state: 'frozen', ySplit: 5 }] });

  sheet.mergeCells('A1:F1');
  const t = sheet.getCell('A1');
  t.value = 'CliniCAT — Historial de uso de insumos';
  t.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
  t.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B1F47' } };
  t.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheet.getRow(1).height = 30;

  sheet.mergeCells('A2:F2');
  const sub = sheet.getCell('A2');
  sub.value = `${rangoTexto(ctx)}  ·  Generado el ${formatDateTime(new Date())}`;
  sub.font = { size: 10, italic: true, color: { argb: 'FFFFFFFF' } };
  sub.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF8B1B5E' } };
  sub.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheet.getRow(2).height = 20;

  sheet.mergeCells('A3:F3');
  sheet.getCell('A3').value = `${filas.length} ${filas.length === 1 ? 'uso' : 'usos'}  ·  ${totalUnidades} unidades en total`;
  sheet.getCell('A3').font = { size: 10, color: { argb: 'FF64748B' } };
  sheet.getCell('A3').alignment = { horizontal: 'left', indent: 1 };
  sheet.getRow(4).height = 8;

  const header = sheet.getRow(5);
  header.values = ['Fecha', 'Producto', 'Cantidad', 'Detalle', 'Área', 'Quién'];
  header.eachCell((cell, col) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5C0F35' } };
    cell.alignment = { horizontal: col === 3 ? 'right' : 'left', vertical: 'middle', indent: 1 };
  });
  header.height = 22;

  filas.forEach((f, idx) => {
    const row = sheet.addRow([f.fecha ?? '', f.producto, f.cantidad, f.detalle || '—', f.area || '(sin área)', f.quien || '—']);
    row.eachCell((cell, col) => {
      cell.alignment = { horizontal: col === 3 ? 'right' : 'left', vertical: 'middle', indent: 1 };
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } } };
      if (idx % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF7F4' } };
    });
    if (f.fecha) row.getCell(1).numFmt = 'dd/mm/yyyy hh:mm';
    row.getCell(3).font = { bold: true, color: { argb: 'FF7B1F47' } };
    if (!f.area) row.getCell(5).font = { italic: true, color: { argb: 'FFB7770A' } };
    row.height = 18;
  });

  [20, 34, 12, 36, 22, 20].forEach((w, i) => (sheet.getColumn(i + 1).width = w));

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `CliniCAT_Uso_insumos_${ymd(new Date())}.xlsx`);
}

export async function exportHistorialUsoPDF(ctx: HistorialUsoExportContext) {
  if (Platform.OS !== 'web') throw new Error('Solo disponible en web por ahora.');
  const filas = ctx.filas;
  const totalUnidades = filas.reduce((s, f) => s + f.cantidad, 0);

  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 24;

  doc.setFillColor(91, 15, 53);
  doc.rect(0, 0, pageWidth, 80, 'F');
  doc.setFillColor(123, 31, 71);
  doc.rect(0, 0, pageWidth * 0.55, 80, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('CliniCAT', margin, 34);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(255, 241, 248);
  doc.text('Medicina Felina', margin, 50);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text('HISTORIAL DE USO', pageWidth - margin, 34, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(232, 182, 207);
  doc.text(rangoTexto(ctx), pageWidth - margin, 50, { align: 'right' });
  doc.text(`${filas.length} usos · ${totalUnidades} unidades`, pageWidth - margin, 62, { align: 'right' });

  const bodyFilas = filas.map((f) => [
    f.fecha ? formatDateTime(f.fecha) : '—',
    f.producto,
    String(f.cantidad),
    f.detalle || '—',
    f.area || '(sin área)',
    f.quien || '—',
  ]);

  autoTable(doc, {
    startY: 100,
    head: [['Fecha', 'Producto', 'Cantidad', 'Detalle', 'Área', 'Quién']],
    body: bodyFilas,
    theme: 'plain',
    headStyles: { fillColor: [91, 15, 53], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9, cellPadding: { top: 7, bottom: 7, left: 6, right: 6 } },
    bodyStyles: { fontSize: 8.5, cellPadding: { top: 6, bottom: 6, left: 6, right: 6 }, lineWidth: 0.3, lineColor: [240, 240, 240], textColor: [30, 41, 59], overflow: 'linebreak' },
    alternateRowStyles: { fillColor: [250, 247, 244] },
    columnStyles: { 0: { cellWidth: 96 }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 56, halign: 'right', fontStyle: 'bold', textColor: [123, 31, 71] }, 3: { cellWidth: 'auto' }, 4: { cellWidth: 80 }, 5: { cellWidth: 76 } },
    margin: { left: margin, right: margin, top: 92 },
    didDrawPage: () => {
      const pageNum = (doc.internal as any).getCurrentPageInfo().pageNumber;
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('CliniCAT — Medicina Felina', margin, pageHeight - 18);
      doc.text(`Página ${pageNum}`, pageWidth - margin, pageHeight - 18, { align: 'right' });
    },
  });

  doc.save(`CliniCAT_Uso_insumos_${ymd(new Date())}.pdf`);
}

// =============================================================================
// JAULAS — historial de préstamos (Excel + PDF)
// =============================================================================
export interface FilaPrestamoJaula {
  jaula: string;
  cliente: string;
  celular: string;
  garantia: string;
  desde: string;
  hasta: string;
  prestadaEl: string;
  presto: string;
  estado: string;
}

export async function exportPrestamosJaulaExcel(filas: FilaPrestamoJaula[]) {
  if (Platform.OS !== 'web') throw new Error('Solo disponible en web por ahora.');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'CliniCAT';
  wb.created = new Date();
  const sheet = wb.addWorksheet('Préstamos de jaulas', { views: [{ state: 'frozen', ySplit: 4 }] });

  sheet.mergeCells('A1:I1');
  const t = sheet.getCell('A1');
  t.value = 'CliniCAT — Historial de préstamos de jaulas';
  t.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
  t.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B1F47' } };
  t.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheet.getRow(1).height = 30;

  sheet.mergeCells('A2:I2');
  sheet.getCell('A2').value = `${filas.length} ${filas.length === 1 ? 'préstamo' : 'préstamos'}  ·  Generado el ${formatDateTime(new Date())}`;
  sheet.getCell('A2').font = { size: 10, italic: true, color: { argb: 'FF64748B' } };
  sheet.getCell('A2').alignment = { horizontal: 'left', indent: 1 };
  sheet.getRow(3).height = 8;

  const header = sheet.getRow(4);
  header.values = ['Jaula', 'Cliente', 'Celular', 'Garantía', 'Desde', 'Hasta', 'Prestada el', 'Prestó', 'Estado'];
  header.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5C0F35' } };
    cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  });
  header.height = 22;

  filas.forEach((f, idx) => {
    const row = sheet.addRow([f.jaula, f.cliente, f.celular, f.garantia, f.desde, f.hasta, f.prestadaEl, f.presto, f.estado]);
    row.eachCell((cell) => {
      cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } } };
      if (idx % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF7F4' } };
    });
    row.height = 18;
  });

  [18, 26, 16, 14, 14, 14, 20, 22, 14].forEach((w, i) => (sheet.getColumn(i + 1).width = w));

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `CliniCAT_Prestamos_jaulas_${ymd(new Date())}.xlsx`);
}

export async function exportPrestamosJaulaPDF(filas: FilaPrestamoJaula[]) {
  if (Platform.OS !== 'web') throw new Error('Solo disponible en web por ahora.');
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape', compress: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 24;

  doc.setFillColor(91, 15, 53);
  doc.rect(0, 0, pageWidth, 70, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('CliniCAT', margin, 32);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('HISTORIAL DE PRÉSTAMOS DE JAULAS', pageWidth - margin, 32, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(232, 182, 207);
  doc.text(`${filas.length} préstamos · ${formatDateTime(new Date())}`, pageWidth - margin, 50, { align: 'right' });

  autoTable(doc, {
    startY: 86,
    head: [['Jaula', 'Cliente', 'Celular', 'Garantía', 'Desde', 'Hasta', 'Prestada el', 'Prestó', 'Estado']],
    body: filas.map((f) => [f.jaula, f.cliente, f.celular, f.garantia, f.desde, f.hasta, f.prestadaEl, f.presto, f.estado]),
    theme: 'plain',
    headStyles: { fillColor: [91, 15, 53], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9, cellPadding: { top: 7, bottom: 7, left: 6, right: 6 } },
    bodyStyles: { fontSize: 8.5, cellPadding: { top: 6, bottom: 6, left: 6, right: 6 }, lineWidth: 0.3, lineColor: [240, 240, 240], textColor: [30, 41, 59], overflow: 'linebreak' },
    alternateRowStyles: { fillColor: [250, 247, 244] },
    margin: { left: margin, right: margin, top: 78 },
    didDrawPage: () => {
      const pageNum = (doc.internal as any).getCurrentPageInfo().pageNumber;
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('CliniCAT — Medicina Felina', margin, pageHeight - 16);
      doc.text(`Página ${pageNum}`, pageWidth - margin, pageHeight - 16, { align: 'right' });
    },
  });

  doc.save(`CliniCAT_Prestamos_jaulas_${ymd(new Date())}.pdf`);
}

// =============================================================================
// PETSHOPS — Excel
// =============================================================================

interface PetshopExportContext {
  petshop: PetshopId;
  petshopNombre?: string;
  ventas: PetshopVenta[];
  rendiciones: PetshopRendicion[];
}

export async function exportPetshopExcel({ petshop, petshopNombre, ventas, rendiciones }: PetshopExportContext) {
  if (Platform.OS !== 'web') throw new Error('Solo disponible en web por ahora.');

  const meta = { nombre: petshopNombre ?? PETSHOPS[petshop]?.nombre ?? petshop };
  const total = ventas.reduce((s, v) => s + (v.monto ?? 0), 0);

  const wb = new ExcelJS.Workbook();
  wb.creator = 'CliniCAT';
  wb.created = new Date();
  wb.company = `CliniCAT — ${meta.nombre}`;

  // ───────── Hoja 1: Ventas del período activo ─────────
  const sheetVentas = wb.addWorksheet(`Ventas ${meta.corto}`, {
    views: [{ state: 'frozen', ySplit: 7 }],
  });

  sheetVentas.mergeCells('A1:E1');
  const t1 = sheetVentas.getCell('A1');
  t1.value = `CliniCAT — ${meta.nombre}`;
  t1.font = { bold: true, size: 18, color: { argb: 'FFFFFFFF' } };
  t1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B1F47' } };
  t1.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheetVentas.getRow(1).height = 32;

  sheetVentas.mergeCells('A2:E2');
  const s1 = sheetVentas.getCell('A2');
  s1.value = `Ventas pendientes de rendir  ·  Generado el ${formatDateTime(new Date())}`;
  s1.font = { size: 10, color: { argb: 'FFFFFFFF' }, italic: true };
  s1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF8B1B5E' } };
  s1.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  sheetVentas.getRow(2).height = 20;

  sheetVentas.getRow(3).height = 8;

  // Total arriba
  sheetVentas.mergeCells('A4:B4');
  sheetVentas.getCell('A4').value = 'TOTAL ACUMULADO';
  sheetVentas.getCell('A4').font = { bold: true, size: 10, color: { argb: 'FF64748B' } };
  sheetVentas.getCell('A4').alignment = { horizontal: 'left', indent: 1 };

  sheetVentas.mergeCells('A5:B5');
  const totalCell = sheetVentas.getCell('A5');
  totalCell.value = total;
  totalCell.numFmt = '"Bs "#,##0.00';
  totalCell.font = { bold: true, size: 22, color: { argb: 'FF7B1F47' } };
  totalCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  totalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCE7F1' } };
  sheetVentas.getRow(5).height = 32;

  sheetVentas.mergeCells('C4:E4');
  sheetVentas.getCell('C4').value = `${ventas.length} ${ventas.length === 1 ? 'venta' : 'ventas'}`;
  sheetVentas.getCell('C4').font = { size: 11, italic: true, color: { argb: 'FF94A3B8' } };
  sheetVentas.getCell('C4').alignment = { horizontal: 'right', vertical: 'middle' };

  sheetVentas.getRow(6).height = 14;

  // Header tabla
  const headerRow = sheetVentas.getRow(7);
  headerRow.values = ['Fecha', 'Producto / descripción', 'Categoría', 'Método pago', 'Monto (Bs)'];
  headerRow.eachCell((cell, col) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5C0F35' } };
    cell.alignment = { horizontal: col === 5 ? 'right' : 'left', vertical: 'middle', indent: 1 };
  });
  headerRow.height = 22;

  const ventasOrd = [...ventas].sort((a, b) => b.fecha.toMillis() - a.fecha.toMillis());
  ventasOrd.forEach((v, idx) => {
    const row = sheetVentas.addRow([
      v.fecha.toDate(),
      v.descripcion,
      (v as any).productoCategoria ?? '—',
      v.metodoPago,
      v.monto,
    ]);
    row.eachCell((cell, col) => {
      cell.alignment = { horizontal: col === 5 ? 'right' : 'left', vertical: 'middle', indent: 1 };
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } } };
      if (idx % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF7F4' } };
    });
    row.getCell(1).numFmt = 'dd/mm/yyyy';
    row.getCell(5).numFmt = '"Bs "#,##0.00';
    row.getCell(5).font = { bold: true, color: { argb: 'FF0F9D58' } };
    row.height = 18;
  });

  [13, 42, 22, 16, 18].forEach((w, i) => (sheetVentas.getColumn(i + 1).width = w));

  // ───────── Hoja 2: Histórico de rendiciones ─────────
  if (rendiciones.length > 0) {
    const sheetRend = wb.addWorksheet('Histórico rendiciones', {
      views: [{ state: 'frozen', ySplit: 4 }],
    });

    sheetRend.mergeCells('A1:G1');
    const t2 = sheetRend.getCell('A1');
    t2.value = `Histórico de rendiciones — ${meta.nombre}`;
    t2.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
    t2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B1F47' } };
    t2.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    sheetRend.getRow(1).height = 28;

    sheetRend.mergeCells('A2:G2');
    sheetRend.getCell('A2').value = `${rendiciones.length} rendiciones · Total acumulado histórico: ${formatBs(rendiciones.reduce((s, r) => s + r.total, 0))}`;
    sheetRend.getCell('A2').font = { size: 10, color: { argb: 'FF64748B' } };
    sheetRend.getCell('A2').alignment = { horizontal: 'left', indent: 1 };

    sheetRend.getRow(3).height = 8;

    const headRend = sheetRend.getRow(4);
    headRend.values = ['Período', 'Ventas', 'Total (Bs)', 'Método', 'Rendida', 'Por', 'Notas'];
    headRend.eachCell((cell, col) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5C0F35' } };
      cell.alignment = { horizontal: col === 3 ? 'right' : 'left', vertical: 'middle', indent: 1 };
    });
    headRend.height = 22;

    rendiciones
      .slice()
      .sort((a, b) => b.rendidaEn.toMillis() - a.rendidaEn.toMillis())
      .forEach((r, idx) => {
        const row = sheetRend.addRow([
          r.mesISO ?? (r as any).mesIso ?? '—',
          r.cantidadVentas,
          r.total,
          r.metodoTransferencia,
          r.rendidaEn.toDate(),
          r.rendidaPorNombre,
          r.notas ?? '',
        ]);
        row.eachCell((cell, col) => {
          cell.alignment = { horizontal: col === 3 ? 'right' : 'left', vertical: 'middle', indent: 1 };
          cell.border = { bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } } };
          if (idx % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF7F4' } };
        });
        row.getCell(3).numFmt = '"Bs "#,##0.00';
        row.getCell(3).font = { bold: true, color: { argb: 'FF7B1F47' } };
        row.getCell(5).numFmt = 'dd/mm/yyyy hh:mm';
        row.height = 18;
      });

    [12, 10, 16, 16, 18, 22, 38].forEach((w, i) => (sheetRend.getColumn(i + 1).width = w));
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `CliniCAT_${meta.corto.replace(/\s+/g, '_')}_${ymd(new Date())}.xlsx`);
}

// =============================================================================
// PETSHOPS — PDF
// =============================================================================

export async function exportPetshopPDF({ petshop, petshopNombre, ventas, rendiciones }: PetshopExportContext) {
  if (Platform.OS !== 'web') throw new Error('Solo disponible en web por ahora.');

  const meta = { nombre: petshopNombre ?? PETSHOPS[petshop]?.nombre ?? petshop };
  const total = ventas.reduce((s, v) => s + (v.monto ?? 0), 0);

  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 24;
  const contentWidth = pageWidth - margin * 2;

  // Header magenta dividido
  doc.setFillColor(91, 15, 53);
  doc.rect(0, 0, pageWidth, 88, 'F');
  doc.setFillColor(123, 31, 71);
  doc.rect(0, 0, pageWidth * 0.55, 88, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('CliniCAT', margin, 36);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 241, 248);
  doc.text('Medicina Felina', margin, 52);
  doc.setFontSize(8);
  doc.text('Calle Los Motojobobos #2015 · +591 60008504', margin, 66);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text('REPORTE PETSHOP', pageWidth - margin, 36, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(meta.nombre, pageWidth - margin, 52, { align: 'right' });
  doc.setFontSize(8);
  doc.setTextColor(232, 182, 207);
  doc.text(`Generado: ${formatDate(new Date())}`, pageWidth - margin, 66, { align: 'right' });

  // Card total grande arriba
  const cardY = 110;
  doc.setFillColor(252, 231, 241);
  doc.roundedRect(margin, cardY, contentWidth, 70, 10, 10, 'F');
  doc.setFillColor(123, 31, 71);
  doc.roundedRect(margin, cardY, contentWidth, 5, 2, 2, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('TOTAL ACUMULADO PENDIENTE DE RENDIR', margin + 18, cardY + 22);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(123, 31, 71);
  doc.text(formatBs(total), margin + 18, cardY + 56);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text(`${ventas.length} ${ventas.length === 1 ? 'venta' : 'ventas'}`, pageWidth - margin - 18, cardY + 56, {
    align: 'right',
  });

  // Tabla ventas
  const headTitleY = cardY + 70 + 24;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(123, 31, 71);
  doc.text('VENTAS DEL PERÍODO', margin, headTitleY);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.line(margin, headTitleY + 6, pageWidth - margin, headTitleY + 6);

  const ventasOrd = [...ventas].sort((a, b) => b.fecha.toMillis() - a.fecha.toMillis());
  const filasVentas = ventasOrd.map((v) => [
    formatDate(v.fecha.toDate()),
    v.descripcion,
    (v as any).productoCategoria ?? '—',
    METODO_LABEL[v.metodoPago] ?? v.metodoPago,
    `+ ${formatBs(v.monto)}`,
  ]);

  autoTable(doc, {
    startY: headTitleY + 14,
    head: [['Fecha', 'Producto / descripción', 'Categoría', 'Método', 'Monto']],
    body: filasVentas,
    theme: 'plain',
    headStyles: {
      fillColor: [91, 15, 53],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: { top: 7, bottom: 7, left: 6, right: 6 },
    },
    bodyStyles: {
      fontSize: 8.5,
      cellPadding: { top: 6, bottom: 6, left: 6, right: 6 },
      lineWidth: 0.3,
      lineColor: [240, 240, 240],
      textColor: [30, 41, 59],
      overflow: 'linebreak',
    },
    alternateRowStyles: { fillColor: [250, 247, 244] },
    columnStyles: {
      0: { cellWidth: 64 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 90 },
      3: { cellWidth: 80 },
      4: { cellWidth: 80, halign: 'right', fontStyle: 'bold', textColor: [15, 157, 88] },
    },
    margin: { left: margin, right: margin, top: 92 },
    didDrawPage: () => {
      const pageNum = (doc.internal as any).getCurrentPageInfo().pageNumber;
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`CliniCAT — ${meta.nombre}`, margin, pageHeight - 18);
      doc.text(`Página ${pageNum}`, pageWidth - margin, pageHeight - 18, { align: 'right' });
    },
  });

  // Histórico de rendiciones (si hay)
  if (rendiciones.length > 0) {
    const lastY = (doc as any).lastAutoTable?.finalY ?? 200;
    let yHist = lastY + 30;
    if (yHist > 700) {
      doc.addPage();
      yHist = 80;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(123, 31, 71);
    doc.text('HISTÓRICO DE RENDICIONES', margin, yHist);
    doc.line(margin, yHist + 6, pageWidth - margin, yHist + 6);

    const filasHist = rendiciones
      .slice()
      .sort((a, b) => b.rendidaEn.toMillis() - a.rendidaEn.toMillis())
      .map((r) => [
        nombreMesISO(r.mesISO ?? (r as any).mesIso),
        String(r.cantidadVentas),
        formatBs(r.total),
        METODO_LABEL[r.metodoTransferencia] ?? r.metodoTransferencia,
        formatDate(r.rendidaEn.toDate()),
        r.rendidaPorNombre,
      ]);

    autoTable(doc, {
      startY: yHist + 14,
      head: [['Período', 'Ventas', 'Total', 'Método', 'Rendida', 'Por']],
      body: filasHist,
      theme: 'plain',
      headStyles: {
        fillColor: [91, 15, 53],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: { top: 6, bottom: 6, left: 6, right: 6 },
      },
      bodyStyles: {
        fontSize: 8.5,
        cellPadding: { top: 5, bottom: 5, left: 6, right: 6 },
        lineWidth: 0.3,
        lineColor: [240, 240, 240],
        textColor: [30, 41, 59],
      },
      alternateRowStyles: { fillColor: [250, 247, 244] },
      columnStyles: {
        2: { halign: 'right', fontStyle: 'bold', textColor: [123, 31, 71] },
      },
      margin: { left: margin, right: margin, top: 92 },
    });
  }

  doc.save(`CliniCAT_${meta.corto.replace(/\s+/g, '_')}_${ymd(new Date())}.pdf`);
}

function nombreMesISO(iso?: string | null): string {
  if (!iso) return '—';
  const [y, m] = String(iso).split('-');
  const nombres = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return `${nombres[Number(m) - 1] ?? m} ${y ?? ''}`;
}
